import { Scene } from 'phaser';
import { criarBotaoAnotacoes, instalarAnotacoes } from '../components/AnotacoesVersus';
import { BotaoArcade } from '../components/BotaoArcade';
import { desenharPaginaLima, desenharVoltar } from '../components/cenario';
import { MolduraPixel } from '../components/MolduraPixel';
import { criarTextoRico } from '../components/TextoRico';
import { SCENES, TEX } from '../config/constants';
import { ARCADE, ARCADE_HEX, ARCADE_TEXT } from '../config/theme';
import { Sessao } from '../services/Sessao';
import { criarSalaVersus, escolherTextoFinal, mudouOPapelNaTela, papelNaTelaDoArgumento, podeEnviarArgumento, ProgressoVersus, rodadaDoDestino } from '../services/versus';
import { t } from '../services/textos';

// Textos da tela (Figma, quadros 81 e 85): no plural para o líder, que escreve pelo grupo.
const INSTRUCOES = {
    lider: ['versus.arg_lider_1', 'versus.arg_lider_2'],
    leitor: ['versus.arg_leitor_1', 'versus.arg_leitor_2']
};
const PRETO = { fundo: ARCADE_HEX.TINTA, fio: ARCADE_HEX.TINTA, fioMeio: ARCADE_HEX.BRANCO };
const BRANCO = { fundo: ARCADE_HEX.BRANCO, fio: ARCADE_HEX.TINTA, fioMeio: ARCADE_HEX.BRANCO };

/**
 * Modo Versus — argumento final e anotações do grupo (Figma, quadros
 * 81–85 e 27). Só o líder escreve e envia; os demais acompanham o texto
 * sendo escrito. Todos registram e leem as anotações.
 */
export class VersusArgumentoScene extends Scene {
    constructor () {
        super(SCENES.VERSUS_ARGUMENTO);
    }

    async create () {
        // `scene.start` repetido reinicia esta mesma instância e o `create` antigo, parado num `await`,
        // retomaria depois do reinício (a cena volta a estar ativa) e desenharia uma 2ª cópia por cima.
        const geracao = this.geracao = (this.geracao ?? 0) + 1;
        if (!Sessao.ativa) {
            this.scene.start(SCENES.LOGIN);
            return;
        }
        this.fonte = this.registry.get('fonte');
        this.progresso = new ProgressoVersus(Sessao.atual.participanteId);
        this.popup = null;
        this.enviando = false;

        desenharPaginaLima(this, { titulo: t('versus.titulo'), rotuloJogador: Sessao.rotulo, rotuloY: 128, espacamento: 0 });
        this.add.image(64, 48, TEX.BTN_INSIGHTS_REDONDO).setOrigin(0)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', () => !this.popup && this.scene.start(SCENES.AGUARDE, {}));

        try {
            this.versus = await this.fonte.obterCatalogoVersus();
        } catch (falha) {
            console.error('[Versus] catálogo indisponível.', falha);
            if (this.scene.isActive()) this.scene.start(SCENES.VERSUS);
            return;
        }
        if (!this.scene.isActive() || geracao !== this.geracao) return;

        // AC-INT-018/020: líder e rodadas encerradas vêm do servidor; sem leitura, o progresso local
        // escolhe a rodada e ninguém escreve (na dúvida, o servidor decide no POST).
        const { turmaId, grupoId } = Sessao.atual;
        this.sala = criarSalaVersus({
            fonte: this.fonte, rede: this.game.network, turmaId, grupoId, rodadas: this.versus.rodadas,
            aoMudar: () => this.aoMudarSala()
        });
        this.events.once('shutdown', () => this.sala.sair());
        await this.sala.entrar();
        if (!this.scene.isActive() || geracao !== this.geracao) return;

        this.rodada = this.sala.lida
            ? rodadaDoDestino(this.sala.destino, this.versus.rodadas)
            : this.progresso.rodadaAtual(this.versus.rodadas);
        if (!this.rodada) {
            // `V12`: o case seguinte espera o facilitador; a tela do Versus mostra a espera.
            this.scene.start(this.sala.destino.etapa === 'aguardando_liberacao' ? SCENES.VERSUS : SCENES.VERSUS_RESULTADO);
            return;
        }
        this.papel = papelNaTelaDoArgumento(this.sala);
        this.lider = this.papel.papel === 'lider';             // RN-MV-12: `eh_lider` do servidor, só mostra

        // O texto já escrito é mantido ao voltar para o chat.
        desenharVoltar(this, 62, 185, () => !this.popup && this.scene.start(SCENES.VERSUS));
        this.add.text(64, 248, t('versus.arg_manchete'), ARCADE_TEXT.manchete).setOrigin(0, 0.5);
        this.add.rectangle(64, 289, 1156, 1, ARCADE_HEX.TINTA).setOrigin(0, 0.5);

        // RI-MV-06: o botão Anotações abre o pop-up de post-its.
        criarBotaoAnotacoes(this, 1111, 249);

        const estilo = { ...ARCADE_TEXT.introducao, fontSize: '26px' };
        let y = 314;
        INSTRUCOES[this.lider ? 'lider' : 'leitor'].map((chave) => t(chave)).forEach((paragrafo) => {
            y += criarTextoRico(this, 64, y, 1156, paragrafo, estilo, { pesoDestaque: '700', entrelinha: 28 }).altura + 27;
        });

        this.desenharCampo();

        const cancelar = this.fonte.aoArgumentoVersus((evento) => this.aoEventoDoArgumento(evento));
        this.events.once('shutdown', cancelar);
        if (!this.lider) this.fonte.acompanharArgumentoVersus(this.rodada.id);
    }

    // --------------------------------------------------------------------- campo

    desenharCampo () {
        const altura = this.lider ? 138 : 212;
        const g = this.add.graphics();
        MolduraPixel.desenhar(g, 60, 513, 1160, altura,
            { fundo: ARCADE_HEX.CAMPO, fio: ARCADE_HEX.FIO_CAMPO, fioMeio: ARCADE_HEX.BRANCO }, { esq: true, dir: true, tamanho: 10 });

        this.area = document.createElement('textarea');
        this.area.className = 'texto-versus';
        this.area.maxLength = this.versus.tetos.caracteresArgumento;
        this.area.style.width = '1118px';
        this.area.style.height = `${altura - 36}px`;
        this.area.value = this.progresso.rodada(this.rodada.id).rascunho;
        // Quem não é líder reentrou no meio da escrita: o evento já passou, o estado traz o último texto.
        if (!this.lider) this.mostrarRascunhoDoEstado();
        this.area.addEventListener('keydown', (evento) => evento.stopPropagation());

        if (this.lider) {
            this.area.placeholder = t('versus.arg_placeholder');
            this.area.setAttribute('aria-label', 'Argumento final');
            this.area.addEventListener('input', () => this.aoDigitar());
        } else {
            // RI-MV-10: para quem não é líder, o campo é só de leitura.
            this.area.readOnly = true;
            this.area.classList.add('vazio-leitura');
            this.area.placeholder = t('versus.arg_placeholder_leitor');
            this.area.setAttribute('aria-label', 'Argumento escrito pelo líder');
        }
        this.dom = this.add.dom(640, 513 + altura / 2, this.area);
        this.aviso = this.add.text(640, 672, '', { ...ARCADE_TEXT.aviso, fontSize: '20px', color: '#9b1c1c' }).setOrigin(0.5);

        if (this.lider) {
            this.botao = new BotaoArcade(this, 640, 721, {
                rotulo: t('versus.enviar'), variante: 'apagado', habilitado: false, aoClicar: () => this.confirmarEnvio()
            });
            this.atualizarBotao();
        } else if (this.papel.papel === 'indefinido') {
            // Sem leitura do estado a tela não fica presa: avisa e deixa tentar de novo (a reconexão
            // também redesenha sozinha quando a leitura enfim funciona).
            this.aviso.setText(this.papel.aviso);
            new BotaoArcade(this, 640, 721, { rotulo: 'TENTAR DE NOVO', aoClicar: () => this.scene.restart() });
        }
    }

    /** RI-MV-11: Enviar fica desabilitado enquanto o argumento estiver vazio. */
    atualizarBotao () {
        this.botao?.setHabilitado(!this.enviando && podeEnviarArgumento(this.area.value, {
            lider: this.lider, maximo: this.versus.tetos.caracteresArgumento
        }));
    }

    aoDigitar () {
        // RN-MV-14: persistência automática enquanto o argumento é produzido.
        this.progresso.salvarRascunho(this.rodada.id, this.area.value);
        this.aviso.setText('');
        this.atualizarBotao();
        // RN-MV-13: os demais integrantes veem o texto em tempo real (sem publicar a cada tecla).
        this.espelho?.remove();
        this.espelho = this.time.delayedCall(350, () => this.fonte.publicarRascunhoVersus({
            rodadaId: this.rodada.id, texto: this.area.value
        }));
    }

    /** Só preenche com texto vindo do servidor; sem rascunho no estado, o campo fica como está. */
    mostrarRascunhoDoEstado () {
        const texto = this.sala.rascunho(this.rodada.ordem);
        if (typeof texto !== 'string' || texto === '') return;
        this.area.value = texto;
        this.area.classList.remove('vazio-leitura');
        this.area.scrollTop = this.area.scrollHeight;
    }

    aoEventoDoArgumento (evento) {
        if (!this.scene.isActive() || evento.rodadaId !== this.rodada.id) return;
        if (evento.tipo === 'rascunho' && !this.lider) {
            this.area.value = evento.texto;
            this.area.classList.toggle('vazio-leitura', evento.texto === '');
            this.area.scrollTop = this.area.scrollHeight;
        }
    }

    /** `AC-INT-020`: a rodada foi encerrada (evento ou reconexão) — todos seguem o `destino` da sala. */
    aoMudarSala () {
        if (!this.rodada || !this.scene.isActive()) return;
        if (this.sala.chatTravado(this.rodada.ordem)) {
            // `IN18`/`AC-INT-065`: o não-líder confirma o texto final do evento; sem texto (facilitador, evento
            // perdido), o último rascunho, como antes. O líder não muda.
            this.avancar(this.lider
                ? this.area.value
                : escolherTextoFinal(this.sala.textoFinal(this.rodada.ordem), this.area.value));
            return;
        }
        // Líder promovido (ou rebaixado) na reconexão, ou a 1ª leitura que enfim deu certo: redesenha.
        if (mudouOPapelNaTela(this.papel, this.sala)) {
            this.scene.restart();
            return;
        }
        // Reconexão: o leitor recupera o que o líder já tinha digitado enquanto estava fora.
        if (!this.lider) this.mostrarRascunhoDoEstado();
    }

    /** RN-MV-15 / RI-MV-13: argumento definitivo; abre o case seguinte ou a espera pela avaliação. */
    avancar (texto = this.area.value) {
        this.progresso.confirmarArgumento(this.rodada.id, texto);
        // `V12`: com o case 2 ainda preso, a tela do Versus mostra a espera pela liberação.
        this.scene.start(this.sala.destino.etapa === 'espera_cedula' ? SCENES.VERSUS_RESULTADO : SCENES.VERSUS);
    }

    // ------------------------------------------------------------------- pop-ups

    /** RI-MV-12: Enviar pede confirmação antes de tornar o argumento definitivo. */
    confirmarEnvio () {
        if (this.popup || this.enviando || !podeEnviarArgumento(this.area.value, {
            lider: this.lider, maximo: this.versus.tetos.caracteresArgumento
        })) return;
        const { camada, fechar } = this.abrirPopup(292, 224, 696, 352, t('versus.confirmar_titulo'));
        camada.add([
            this.add.text(640, 365, t('versus.confirmar_pergunta'), ARCADE_TEXT.textoPopup).setOrigin(0.5),
            this.add.text(640, 421, t('versus.confirmar_aviso'), ARCADE_TEXT.textoPopup).setOrigin(0.5),
            new BotaoArcade(this, 492, 504, { rotulo: t('geral.cancelar'), largura: 280, cores: PRETO, aoClicar: fechar }),
            new BotaoArcade(this, 788, 504, {
                rotulo: t('versus.enviar'), largura: 280, aoClicar: () => {
                    fechar();
                    this.enviar();
                }
            })
        ]);
    }

    async enviar () {
        const texto = this.area.value.trim();
        this.enviando = true;
        this.atualizarBotao();
        try {
            // `AC-INT-019`: a decisão (enviado / recusada / rodada encerrada) é da sala; a cena só executa.
            const resultado = await this.sala.enviarArgumento({ ordem: this.rodada.ordem, texto });
            if (!this.scene.isActive()) return;
            if (resultado.estado === 'recusada') {
                this.enviando = false;
                this.lider = this.sala.podeEnviarArgumento;
                this.area.readOnly = !this.lider;
                this.atualizarBotao();
                this.aviso.setText(resultado.mensagem);
                return;
            }
            this.avancar(resultado.texto);
        } catch (falha) {
            console.error('[Versus] argumento não enviado.', falha);
            if (!this.scene.isActive()) return;
            this.enviando = false;
            this.atualizarBotao();
            this.aviso.setText(falha?.status === 403
                ? t('versus.erro_so_lider')
                : t('versus.erro_enviar'));
        }
    }
}

instalarAnotacoes(VersusArgumentoScene);
